import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export const useGoogleAuth = () => {
  const [loading, setLoading] = useState(false);
  const [showGoogleCompanyPrompt, setShowGoogleCompanyPrompt] = useState(false);
  const [companyName, setCompanyName] = useState("");
  const navigate = useNavigate();

  const handleGoogleSignIn = async (teamMemberId: string | null = null) => {
    try {
      setLoading(true);

      // Store the team_member_id in session storage as a fallback
      if (teamMemberId) {
        sessionStorage.setItem("pending_team_invite", teamMemberId);
      }

      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}/auth`,
          // Include team_member_id in query params as a fallback
          queryParams: teamMemberId
            ? { team_member_id: teamMemberId }
            : undefined,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      toast.error(error.message);
      setLoading(false);
    }
  };

  const submitGoogleCompanyName = async () => {
    // Trim the company name to handle whitespace
    const trimmedCompanyName = companyName.trim();

    if (!trimmedCompanyName) {
      toast.error("Please enter a company name");
      return;
    }

    try {
      setLoading(true);
      console.log("Submitting company name:", trimmedCompanyName);

      // 1. Get the current user
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) {
        throw new Error("User not found");
      }

      console.log("Current user:", user);

      // 2. Update user metadata with company name
      const { error: updateError } = await supabase.auth.updateUser({
        data: { company_name: trimmedCompanyName },
      });

      if (updateError) {
        console.error("Error updating user metadata:", updateError);
        throw updateError;
      }

      // 3. Generate a unique slug from the company name
      const slug = trimmedCompanyName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, "");

      console.log("Generated slug:", slug);

      // 4. Create the company in the database
      const { data: company, error: companyError } = await supabase
        .from("companies")
        .insert({
          name: trimmedCompanyName,
          slug: slug,
          owner_id: user.id,
        })
        .select()
        .single();

      if (companyError) {
        console.error("Error creating company:", companyError);
        throw companyError;
      }

      console.log("Company created:", company);

      // 5. Update profile with company_id to ensure the association is saved
      const { error: profileError } = await supabase
        .from("profiles")
        .update({ company_id: company.id })
        .eq("id", user.id);

      if (profileError) {
        console.error("Error updating profile:", profileError);
        throw profileError;
      }

      // 5.5. Check for Whop license and update company plan to enterprise if found
      const whopLicenseKey = localStorage.getItem('whop_license_key');
      const whopMembershipData = localStorage.getItem('whop_membership_data');

      if (whopLicenseKey && whopMembershipData) {
        try {
          console.log("Applying Whop license to company...");
          const membershipData = JSON.parse(whopMembershipData);

          // Update company to enterprise plan with license info
          const { error: updateCompanyError } = await supabase
            .from("companies")
            .update({
              plan: 'enterprise',
              whop_license_key: whopLicenseKey,
              whop_membership_id: membershipData.id,
              whop_validated_at: new Date().toISOString(),
              whop_metadata: membershipData,
            })
            .eq("id", company.id);

          if (updateCompanyError) {
            console.error("Error updating company with license:", updateCompanyError);
            toast.error("Failed to apply license to company");
          } else {
            console.log("Company updated with Whop license successfully");

            // Bind the license with metadata
            const { error: bindError } = await supabase.functions.invoke('validate-whop-license', {
              body: {
                licenseKey: whopLicenseKey,
                companyId: company.id,
                preSignupValidation: false,
              },
            });

            if (bindError) {
              console.error("Error binding license:", bindError);
            } else {
              console.log("License bound to company successfully");
            }

            // Clean up localStorage
            localStorage.removeItem('whop_license_key');
            localStorage.removeItem('whop_membership_data');

            toast.success("Enterprise license activated!");
          }
        } catch (error) {
          console.error("Error processing Whop license:", error);
        }
      }

      // 6. Add user as a team member BEFORE creating statuses
      const { data: existingMember, error: lookupError } = await supabase
        .from("team_members")
        .select("id, user_id")
        .eq("company_id", company.id)
        .or(`user_id.eq.${user.id},email.eq.${user.email?.toLowerCase()}`)
        .maybeSingle();

      if (lookupError) {
        console.error("Error checking for existing team member:", lookupError);
      } else if (existingMember) {
        // If a team member record exists but doesn't have user_id, update it
        if (!existingMember.user_id) {
          console.log("Updating existing team member record with user_id");
          const { error: updateError } = await supabase
            .from("team_members")
            .update({
              user_id: user.id,
              name: user.email?.split("@")[0] || "Team Member",
              status: 'active',
              updated_at: new Date().toISOString()
            })
            .eq("id", existingMember.id);

          if (updateError) {
            console.error("Error updating team member:", updateError);
            throw updateError;
          } else {
            console.log("Team member record updated successfully");
          }
        } else {
          console.log("User is already a team member, skipping duplicate entry");
        }
      } else {
        // No existing record, create a new one (owner scenario)
        const { error: teamMemberError } = await supabase
          .from("team_members")
          .insert({
            company_id: company.id,
            email: user.email?.toLowerCase(),
            name: user.email?.split("@")[0] || "Team Member",
            user_id: user.id,
            status: 'active',
            invited_by: user.id,
            created_at: new Date().toISOString()
          });

        if (teamMemberError) {
          console.error("Error adding user to team members:", teamMemberError);
          throw teamMemberError;
        } else {
          console.log("User added to team members successfully");
        }
      }

      // 7. Create default statuses for the company (after user is a team member)
      // Create default statuses for the company (same as email/password flow)
      const defaultStatuses = [
        { name: "In Review", color: "#94a3b8", description: "", position: 0 },
        { name: "Planned", color: "#60a5fa", description: "", position: 1 },
        { name: "In Progress", color: "#f59e0b", description: "", position: 2 },
        { name: "Completed", color: "#10b981", description: "", position: 3 },
        { name: "Rejected", color: "#ef4444", description: "", position: 4 },
      ];

      const { error: statusError } = await supabase
        .from("company_statuses")
        .insert(
          defaultStatuses.map((status) => ({
            ...status,
            company_id: company.id,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            position: status.position,
          }))
        );

      if (statusError) {
        console.error("Error creating default statuses:", statusError);
        throw statusError;
      } else {
        console.log("Default statuses created successfully");
      }

      // 6. Refresh the session to ensure updated metadata is available
      await supabase.auth.refreshSession();

      // 7. Wait a moment for all database operations to complete
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Track Google Ads conversion for signup
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }

      toast.success("Company created successfully!");
      setShowGoogleCompanyPrompt(false);

      // 8. Navigate to admin dashboard
      navigate("/admin");
    } catch (error: any) {
      console.error("Error creating company:", error);
      toast.error(error.message || "Failed to create company");
    } finally {
      setLoading(false);
    }
  };

  const checkGoogleAuth = async (teamMemberId: string | null = null) => {
    try {
      const { data: sessionData, error: sessionError } =
        await supabase.auth.getSession();

      if (sessionError) {
        console.error("Error checking session:", sessionError);
        return false;
      }

      // Early return if no session or not Google auth
      if (!sessionData.session?.provider_token) {
        return false;
      }

      // Get the current user ID and email
      const userId = sessionData.session.user.id;
      const userEmail = sessionData.session.user.email?.toLowerCase();

      if (!userId || !userEmail) {
        console.error("No user ID or email found in session");
        return false;
      }

      // If there's a team invitation, handle that first
      if (teamMemberId) {
        console.log("Team invitation detected with ID:", teamMemberId);

        // Get the team member details including email and status
        const { data: teamMember, error: teamError } = await supabase
          .from("team_members")
          .select("company_id, email, status, user_id")
          .eq("id", teamMemberId)
          .single();

        if (teamError) {
          console.error("Error fetching team member:", teamError);
          toast.error("Invalid or expired invitation");
          return false;
        }

        // Check if the invitation is already accepted
        if (teamMember.status === "active" && teamMember.user_id) {
          if (teamMember.user_id === userId) {
            toast.info("You've already accepted this invitation");
          } else {
            toast.error(
              "This invitation has already been accepted by another user"
            );
          }
          navigate("/admin");
          return true;
        }

        // Verify the user's email matches the invitation
        const userEmail = sessionData.session.user.email?.toLowerCase();
        if (userEmail !== teamMember.email?.toLowerCase()) {
          toast.error(
            "Please sign in with the email address that received the invitation"
          );
          return false;
        }

        // 1. First, update the user's profile with the team's company ID
        const { error: profileError } = await supabase
          .from("profiles")
          .upsert(
            {
              id: userId,
              company_id: teamMember.company_id,
              updated_at: new Date().toISOString(),
            },
            {
              onConflict: "id",
            }
          )
          .select()
          .single();

        if (profileError) {
          console.error("Error updating profile:", profileError);
          toast.error("Failed to update your profile");
          return false;
        }

        // 2. Activate the team member status
        const { error: teamUpdateError } = await supabase
          .from("team_members")
          .update({
            user_id: userId,
            status: "active",
            updated_at: new Date().toISOString(),
          })
          .eq("id", teamMemberId);

        if (teamUpdateError) {
          console.error("Error activating team membership:", teamUpdateError);
          toast.error("Failed to activate team membership");
          return false;
        }

        console.log("Team membership activated successfully");
        toast.success("Successfully joined the team!");

        // 3. Ensure the session is refreshed with the latest profile data
        await supabase.auth.refreshSession();
        
        // Show success message and refresh the page to ensure all data is loaded
        toast.success("Successfully joined the team! Refreshing...");
        
        // Small delay to show the toast before refreshing
        setTimeout(() => {
          window.location.href = "/admin";
        }, 1000);
        
        return true;
      }

      console.log("Checking profile for user:", sessionData.session.user.id);

      // Check if user has a company in the database
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("company_id")
        .eq("id", userId)
        .maybeSingle();

      if (profileError) {
        console.error("Error checking profile:", profileError);
        return false;
      }

      // If no profile exists, create one
      if (!profile) {
        const { error: createProfileError } = await supabase
          .from("profiles")
          .insert({
            id: userId,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          });

        if (createProfileError) {
          console.error("Error creating profile:", createProfileError);
          return false;
        }

        // Only show company prompt if no team invitation
        console.log("New profile created, showing company prompt");
        setShowGoogleCompanyPrompt(true);
        return true;
      }

      if (!profile?.company_id && !teamMemberId) {
        console.log("No company_id found in user profile");
        setShowGoogleCompanyPrompt(true);
        return true;
      }

      // If user has a company, redirect to admin
      console.log("User has company_id:", profile?.company_id);
      navigate("/admin");
      return false;
    } catch (error) {
      console.error("Error in checkGoogleAuth:", error);
      return false;
    }
  };

  // Add an effect to check auth status on mount and when URL changes
  useEffect(() => {
    const handleAuthStateChange = async (event: string, session: any) => {
      if (event === "SIGNED_IN" && session?.provider_token) {
        let teamMemberId: string | null = null;

        // 1. Try to get from URL search params (direct link)
        const urlParams = new URLSearchParams(window.location.search);
        teamMemberId = urlParams.get("team_member_id");

        // 2. Try to get from session storage (set before OAuth redirect)
        if (!teamMemberId) {
          teamMemberId = sessionStorage.getItem("pending_team_invite");
          if (teamMemberId) {
            // Clean up after getting the value
            sessionStorage.removeItem("pending_team_invite");
          }
        }

        // 3. Try to get from OAuth state (Google OAuth)
        if (!teamMemberId) {
          const hash = window.location.hash;
          const hashParams = new URLSearchParams(
            hash.substring(hash.indexOf("?") + 1)
          );
          const stateParam = hashParams.get("state");

          if (stateParam) {
            try {
              const state = JSON.parse(decodeURIComponent(stateParam));
              if (state.team_member_id) {
                teamMemberId = state.team_member_id;
              }
            } catch (e) {
              console.log("No team_member_id in state");
            }
          }
        }

        console.log("Processing team invitation with ID:", teamMemberId);

        // User just signed in with Google, check their company status
        const success = await checkGoogleAuth(teamMemberId);

        // If we had a team member ID but checkGoogleAuth returned false, something went wrong
        if (teamMemberId && !success) {
          console.error("Failed to process team invitation");
          toast.error("Failed to process team invitation. Please try again.");
        }
      }
    };

    // Subscribe to auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(handleAuthStateChange);

    // Check auth status immediately
    const urlParams = new URLSearchParams(window.location.search);
    const teamMemberId = urlParams.get("team_member_id");
    checkGoogleAuth(teamMemberId);

    // Cleanup subscription
    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return {
    loading,
    companyName,
    setCompanyName,
    handleGoogleSignIn,
    submitGoogleCompanyName,
    showGoogleCompanyPrompt,
    setShowGoogleCompanyPrompt,
    checkGoogleAuth,
  };
};
